import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Play, X, Copy, Check, MoreVertical } from 'lucide-react';
import BrandSelect from 'features/playground/components/BrandSelect';
import ProviderLogo from 'features/playground/components/ProviderLogo';
import ModelConfigPopup from 'features/playground/components/ModelConfigPopup';
import PopMenu from 'features/playground/components/PopMenu';
import BrandLoader from 'shared/components/ui/BrandLoader';
import MessagesEditor from 'features/prompts/components/MessagesEditor';
import JsonEditor from 'features/prompts/components/JsonEditor';
import { OutputFormatMenu, OutputSchemaEditor, kindOf as outputKindOf } from 'features/prompts/components/OutputFormatPanel';
import {
  newMessage, reconcileVariables, messageText, setMessageText, sectionInnerNames,
} from 'features/prompts/lib/promptDraft';
import { getPrompt as getPromptDetail } from 'features/prompts/api/promptsApi';
import { modelFromPromptVersion } from 'features/playground/lib/promptImport';
import {
  activityToolbarButtonClass, primaryButtonClass, drawerIconButtonClass, sectionTitleClass,
  inputClass, menuItemClass, modalClass, modalOverlayStyle, textButtonClass,
} from 'features/playground/styles';

/**
 * ComparePanel — one independent task on the comparison bench.
 *
 * A task chooses its input SOURCE (Manual / From span / From prompt), its model
 * + params, and — when it uses variables — their values. It owns its messages
 * (editable inline, `{{var}}` highlighted). Output does NOT live here: it renders
 * in the shared Results section below the bench, aligned under each task.
 *
 * Controlled: all chosen state lives in `panel` (parent-owned).
 */

// Input sources: the same three the playground has always had, now per task.
const SOURCES = [
  ['manual', 'Manual'],
  ['span', 'From span'],
  ['prompt', 'From prompt'],
];

const integrationModels = (integ) => {
  const list = Array.isArray(integ?.config?.models) ? integ.config.models : [];
  if (list.length) return list;
  return integ?.default_model ? [integ.default_model] : [];
};

export default function ComparePanel({
  panel, index, onChange, onRemove, onDuplicate, onRun, onSavePrompt, onRequestSpanImport,
  integrations, modelsForCredential, prompts, canRemove, running,
}) {
  const [versions, setVersions] = useState([]);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [paramsOpen, setParamsOpen] = useState(false);
  const [paramsAnchor, setParamsAnchor] = useState(null);
  const [varsOpen, setVarsOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [outputOpen, setOutputOpen] = useState(false);
  const [copiedSpan, setCopiedSpan] = useState(false);

  const copySpanId = async () => {
    if (!panel.source_span_id) return;
    try {
      await navigator.clipboard.writeText(panel.source_span_id);
      setCopiedSpan(true);
      setTimeout(() => setCopiedSpan(false), 1400);
    } catch (_) { /* clipboard blocked */ }
  };

  const mode = panel.mode || 'manual';

  const integById = useMemo(
    () => Object.fromEntries(integrations.map((x) => [String(x.id), x])),
    [integrations],
  );

  const model = panel.model || {};
  const hasProvider = Boolean(model.credential_id);
  const modelsForRow = modelsForCredential(integById[String(model.credential_id)]);

  // Variables are (re)derived from the current messages as the user types, merged
  // with declarations carried in from an imported version. Hint-only.
  //
  // Two kinds are excluded from the value inputs: placeholders (message lists
  // are injected by the runner, not typed here) and per-item section fields —
  // those are supplied by each item of their {{#section}}'s data, so an input
  // for them would collect a value the run never uses as typed.
  const variables = useMemo(() => {
    const inner = sectionInnerNames(panel.messages || []);
    return reconcileVariables(panel.messages || [], panel.variables || [])
      .filter((v) => v.type !== 'messages' && !inner.has(v.name));
  }, [panel.messages, panel.variables]);

  // Refetch the version list whenever the linked prompt OR its selected version
  // changes — so a version just saved from here shows up in the dropdown (and
  // stays selected) instead of leaving the field blank.
  useEffect(() => {
    if (!panel.source?.promptId) { setVersions([]); return undefined; }
    let alive = true;
    getPromptDetail(panel.source.promptId)
      .then((d) => { if (alive) setVersions(d.versions || []); })
      .catch(() => { if (alive) setVersions([]); });
    return () => { alive = false; };
  }, [panel.source?.promptId, panel.source?.versionNo]);

  const patch = (p) => onChange(p);
  const patchModel = (p) => onChange({ model: { ...model, ...p } });

  const setMode = (next) => {
    if (next === mode) return;
    if (next === 'span') { onRequestSpanImport(); patch({ mode: next }); return; }
    if (next === 'manual') { patch({ mode: next, source: null }); return; }
    patch({ mode: next });
  };

  const loadVersionInto = (detail, v, promptName) => {
    onChange({
      mode: 'prompt',
      source: { promptId: detail.id, promptName, versionNo: v?.version_no ?? null },
      messages: (v?.messages && v.messages.length) ? v.messages : [newMessage('system')],
      variables: v?.variables || [],
      varValues: {},
      tools: v?.tools || [],
      response_format: v?.response_format ?? null,
      // Snapshot of the imported version — the save modal diffs against this.
      baseline: v ? {
        messages: v.messages || [], model: v.model || {}, variables: v.variables || [],
        tools: v.tools || [], response_format: v.response_format ?? null, version_hash: v.version_hash,
      } : null,
      // A prompt's model block is open JSON and may name a provider we hold no
      // key for. Adopt only what this team can actually call — see
      // features/playground/lib/promptImport.
      model: modelFromPromptVersion(v?.model, {
        integrations, modelsForCredential, current: model,
      }),
      dirty: false,
    });
  };

  const selectPrompt = async (promptId) => {
    if (!promptId) { patch({ source: null }); return; }
    setLoadingPrompt(true);
    try {
      const detail = await getPromptDetail(promptId);
      const vs = detail.versions || [];
      setVersions(vs);
      const latest = vs.find((v) => v.version_no === detail.current_version) || vs[0];
      loadVersionInto(detail, latest, detail.name);
    } catch (_) { /* leave panel untouched */ } finally { setLoadingPrompt(false); }
  };

  const selectVersion = (versionNo) => {
    const v = versions.find((x) => String(x.version_no) === String(versionNo));
    if (v && panel.source) loadVersionInto({ id: panel.source.promptId }, v, panel.source.promptName);
  };

  const setVarValue = (name, value) =>
    patch({ varValues: { ...(panel.varValues || {}), [name]: value } });

  const editMessages = (next) => patch({ messages: next, dirty: Boolean(panel.source) });
  const addMessage = () => editMessages([...(panel.messages || []), newMessage('user')]);

  // Insert a fresh {{variable}} token into the last message, so it shows up in the
  // values list immediately. The user can rename it in the text.
  const insertVariable = () => {
    const existing = new Set(variables.map((v) => v.name));
    let n = 'variable';
    let i = 1;
    while (existing.has(n)) { i += 1; n = `variable${i}`; }
    const msgs = (panel.messages && panel.messages.length) ? panel.messages : [newMessage('user')];
    const lastIdx = msgs.length - 1;
    const text = messageText(msgs[lastIdx]);
    const nextText = `${text}${text && !/\s$/.test(text) ? ' ' : ''}{{${n}}}`;
    editMessages(msgs.map((m, idx) => (idx === lastIdx ? setMessageText(m, nextText) : m)));
  };

  return (
    <section className="flex h-full min-w-[360px] flex-1 flex-col overflow-hidden">
      {/* header */}
      <div className="flex h-12 shrink-0 items-center gap-1 px-4">
        <input
          value={panel.title || ''}
          onChange={(e) => patch({ title: e.target.value })}
          placeholder={index === 0 ? 'Base task' : 'Comparison task'}
          aria-label="Task title"
          className="-ml-1.5 h-8 min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1.5 text-[13px] font-semibold text-[color:var(--text-primary)] placeholder:font-normal placeholder:text-[color:var(--text-disabled)] hover:border-[color:var(--border-default)] focus:border-[color:var(--border-strong)] focus:outline-none"
        />
        {panel.source_span_id && (
          <button type="button" onClick={copySpanId}
            title={`Span ${panel.source_span_id}: click to copy`}
            aria-label="Copy span ID"
            className={`inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-1.5 font-mono text-[11px] text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] ${focusRing}`}>
            {copiedSpan ? <Check size={11} /> : <Copy size={11} />}
            {`${String(panel.source_span_id).slice(0, 3)}…${String(panel.source_span_id).slice(-3)}`}
          </button>
        )}
        <button type="button" onClick={onRun} disabled={running} title="Run this task" aria-label="Run this task"
          className={drawerIconButtonClass}>
          {running ? <BrandLoader label={null} size="sm" /> : <Play size={14} />}
        </button>
        <PopMenu
          label="Task options"
          width={176}
          renderTrigger={({ ref, onClick, ariaProps }) => (
            <button ref={ref} type="button" onClick={onClick} title="Task options" aria-label="Task options"
              className={drawerIconButtonClass} {...ariaProps}>
              <MoreVertical size={15} />
            </button>
          )}
        >
          {(close) => (
            <>
              <div className="px-3 pb-1 pt-1.5 text-[11px] font-semibold text-[color:var(--text-muted)]">Source</div>
              {SOURCES.map(([key, label]) => (
                <button key={key} type="button" role="menuitemradio" aria-checked={mode === key}
                  onClick={() => { close(); setMode(key); }}
                  className={menuItemClass}>
                  <span className="flex-1">{label}</span>
                  {mode === key && <Check size={13} />}
                </button>
              ))}
              <div className="my-1 border-t border-[color:var(--border-subtle)]" />
              <button type="button" role="menuitem" onClick={() => { close(); onDuplicate(); }} className={menuItemClass}>
                Duplicate
              </button>
              {canRemove && (
                <button type="button" role="menuitem" onClick={() => { close(); onRemove(); }}
                  className={`${menuItemClass} text-[color:var(--feedback-error)]`}>
                  Remove
                </button>
              )}
            </>
          )}
        </PopMenu>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4">
        {/* prompt picker (prompt mode only): name + version + edited flag */}
        {mode === 'prompt' && (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <BrandSelect
                value={panel.source?.promptId || ''}
                onChange={selectPrompt}
                placeholder={loadingPrompt ? 'Loading' : 'Select a prompt'}
                ariaLabel="Prompt"
                minWidth={0}
                options={(prompts || []).map((p) => ({ value: p.id, label: p.name }))}
              />
            </div>
            {panel.source?.promptId && versions.length > 0 && (
              <BrandSelect
                value={panel.source.versionNo ?? ''}
                onChange={selectVersion}
                ariaLabel="Prompt version"
                minWidth={96}
                options={versions.map((v) => ({
                  value: v.version_no,
                  label: `v${v.version_no}${(v.labels || []).length ? ` · ${v.labels.join(', ')}` : ''}`,
                }))}
              />
            )}
            {panel.source?.promptId && panel.dirty && (
              <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]" title="Edited since import">Edited</span>
            )}
          </div>
        )}

        {/* model: provider, model and Params on one line */}
        <div className="flex flex-col gap-3">
          <h3 className={sectionTitleClass}>Model</h3>
          {integrations.length === 0 ? (
            <p className="text-[12px] text-[color:var(--text-muted)]">No linked providers: add one under Integrations.</p>
          ) : (
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <BrandSelect
                  value={model.credential_id || ''}
                  onChange={(credId) => {
                    const integ = integById[String(credId)];
                    patchModel({
                      credential_id: integ?.id || '',
                      provider: integ?.provider || '',
                      model: integrationModels(integ)[0] || '',
                    });
                  }}
                  placeholder="Provider"
                  ariaLabel="Provider"
                  minWidth={0}
                  options={integrations.map((integ) => ({
                    value: integ.id,
                    label: `${integ.label} (${integ.provider})`,
                    icon: <ProviderLogo provider={integ.provider} size={16} />,
                  }))}
                />
              </div>
              {hasProvider && (
                <>
                  <div className="min-w-0 flex-1">
                    <BrandSelect
                      value={model.model || ''}
                      onChange={(v) => patchModel({ model: v })}
                      placeholder="Select a model"
                      ariaLabel="Model"
                      minWidth={0}
                      leadingIcon={<ProviderLogo provider={model.provider} size={16} />}
                      options={modelsForRow.map((m) => ({
                        value: m, label: m, icon: <ProviderLogo provider={model.provider} size={16} />,
                      }))}
                    />
                  </div>
                  <button
                    type="button"
                    title="Model parameters"
                    aria-haspopup="dialog"
                    aria-expanded={paramsOpen}
                    onClick={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      // Open anchored just under the button (right edges aligned),
                      // clamped to stay on screen.
                      setParamsAnchor({
                        x: Math.max(8, Math.min(r.right - 300, window.innerWidth - 308)),
                        y: r.bottom + 6,
                      });
                      setParamsOpen(true);
                    }}
                    className={`${activityToolbarButtonClass} shrink-0`}>
                    Params
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* messages: add / variables / tools / output format */}
        <div className="flex flex-col gap-3">
          <h3 className={sectionTitleClass}>Messages</h3>
          <div className="flex flex-wrap items-center gap-2">
            <ToolbarButton onClick={addMessage}>Message</ToolbarButton>
            {variables.length > 0 && (
              <ToolbarButton count={variables.length} onClick={() => setVarsOpen(true)}>Variables</ToolbarButton>
            )}
            <ToolbarButton count={(panel.tools || []).length || null} expanded={toolsOpen} onClick={() => setToolsOpen((o) => !o)}>Tools</ToolbarButton>
            {/* the prompts feature owns this menu: restyled from here to the toolbar's h-8 */}
            <div className="[&>div>button]:h-8 [&>div>button]:rounded-md [&>div>button]:px-3 [&>div>button]:text-[12px] [&>div>button]:text-[color:var(--text-primary)] [&>div>button]:hover:bg-surface-hover [&>div>button>svg:first-child]:hidden">
              <OutputFormatMenu
                value={panel.response_format ?? null}
                onChange={(response_format) => patch({ response_format, dirty: Boolean(panel.source) })}
                onPick={(k) => setOutputOpen(k === 'structured')}
              />
            </div>
          </div>
          {toolsOpen && (
            <JsonEditor
              value={panel.tools || []}
              emptyValue={[]}
              onChange={(tools) => patch({ tools, dirty: Boolean(panel.source) })}
            />
          )}
          {outputOpen && outputKindOf(panel.response_format) === 'structured' && (
            <OutputSchemaEditor
              value={panel.response_format}
              onChange={(response_format) => patch({ response_format, dirty: Boolean(panel.source) })}
            />
          )}
          <MessagesEditor
            messages={(panel.messages && panel.messages.length) ? panel.messages : [newMessage('system')]}
            onChange={editMessages}
          />
        </div>
      </div>

      {/* save: pinned to the column bottom, always visible regardless of scroll */}
      <div className="flex h-12 shrink-0 items-center gap-3 border-t border-[color:var(--border-subtle)] px-4">
        <button type="button" onClick={onSavePrompt} disabled={panel.saving}
          className={`${activityToolbarButtonClass} disabled:opacity-50`}>
          {panel.source ? 'Save new version' : 'Save as prompt'}
        </button>
        {panel.savedNote && (
          <span role="status" className="text-[12px] text-[color:var(--feedback-success)]">{panel.savedNote}</span>
        )}
      </div>

      {/* variable values: opened from the Messages toolbar, so the values
          don't clutter the task column */}
      {varsOpen && (
        <VariablesModal
          title={panel.title}
          variables={variables}
          values={panel.varValues || {}}
          onValue={setVarValue}
          onAdd={insertVariable}
          onClose={() => setVarsOpen(false)}
        />
      )}

      {/* draggable model parameters popup (opened anchored under the Params button) */}
      {paramsOpen && (
        <ModelConfigPopup
          model={model}
          provider={model.provider}
          anchor={paramsAnchor}
          onChange={patchModel}
          onClose={() => setParamsOpen(false)}
        />
      )}
    </section>
  );
}

const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

/** A neutral h-8 config button (Message / Variables / Tools). */
function ToolbarButton({ children, count, expanded, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      className={activityToolbarButtonClass}
    >
      {children}
      {count != null && <span className="text-[11px] font-normal text-[color:var(--text-muted)]">{count}</span>}
    </button>
  );
}

/** Variable values for one task. */
function VariablesModal({ title, variables, values, onValue, onAdd, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={modalOverlayStyle}
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Variables"
        className={`${modalClass} max-w-[440px] p-5 text-[color:var(--text-primary)]`}
        style={{ fontFamily: 'Inter, sans-serif' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-[14px] font-semibold">Variables{title ? ` · ${title}` : ''}</h2>
          <button type="button" onClick={onClose} aria-label="Close" title="Close"
            className={`${drawerIconButtonClass} -mr-1 -mt-1`}>
            <X size={15} />
          </button>
        </div>
        <div className="mt-4 max-h-[60vh] overflow-y-auto">
          {variables.length === 0 ? (
            <p className="py-4 text-center text-[12px] text-[color:var(--text-muted)]">
              No variables yet. Add one below, or type <span className="font-mono">{'{{name}}'}</span> in a message.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {variables.map((v, i) => (
                <label key={v.name} className="flex flex-col gap-1">
                  <span className="font-mono text-[11px] text-[color:var(--text-muted)]">{`{{${v.name}}}`}</span>
                  <input
                    value={values[v.name] || ''}
                    onChange={(e) => onValue(v.name, e.target.value)}
                    placeholder={v.default != null && v.default !== '' ? `default: ${v.default}` : 'value'}
                    className={inputClass}
                    autoFocus={i === 0} // eslint-disable-line jsx-a11y/no-autofocus
                  />
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="mt-5 flex items-center justify-between gap-2">
          <button type="button" onClick={onAdd} className={textButtonClass}>Add variable</button>
          <button type="button" onClick={onClose} className={primaryButtonClass}>Done</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
